export const CRM_LOCAL_SYNC_URL = 'http://localhost:29876/api/sync';
export const CRM_LOCAL_CLICK_ROUTE = '#/dashboard';
export const CRM_NOTIFY_DEDUPE_MS = 5000;
export const CRM_NOTIFY_STORAGE_PREFIX = 'profase:notify:';

export type CrmLocalNotification = {
  id?: string;
  titulo: string;
  mensaje: string;
  data: unknown;
  detalles: string;
  onClick: string;
};

export type CrmLocalNotificationInput = {
  id?: string;
  messageId?: string;
  titulo?: string;
  mensaje?: string;
  data?: unknown;
  detalles?: string;
  origin?: string;
  ruta?: string;
};

type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

export function resolveCrmOrigin(origin?: string): string {
  const resolved =
    origin ||
    (typeof window !== 'undefined' && window.location?.origin) ||
    '';
  return resolved.replace(/\/$/, '');
}

export function resolveCrmOnClick(origin?: string, ruta = CRM_LOCAL_CLICK_ROUTE): string {
  const base = resolveCrmOrigin(origin);
  const hash = ruta.startsWith('#') ? ruta : `#/${ruta.replace(/^\//, '')}`;
  return `${base}/${hash}`;
}

export function crmNotificationFingerprint(input: {
  id?: string;
  messageId?: string;
  titulo?: string;
  mensaje?: string;
} = {}): string {
  const messageId = (input.messageId || input.id || '').trim();
  if (messageId) return `id:${messageId}`;
  const titulo = (input.titulo || '').trim();
  const mensaje = (input.mensaje || '').trim();
  return `msg:${titulo}|${mensaje}`;
}

export function shouldDeliverCrmNotification(
  fingerprint: string,
  now = Date.now(),
  storage?: StorageLike | null,
  windowMs = CRM_NOTIFY_DEDUPE_MS,
): boolean {
  const store = storage ?? (typeof localStorage === 'undefined' ? null : localStorage);
  if (!store) return true;

  const key = CRM_NOTIFY_STORAGE_PREFIX + fingerprint;
  const prev = Number(store.getItem(key) || 0);
  if (prev && now - prev < windowMs) return false;
  store.setItem(key, String(now));
  return true;
}

export function buildCrmLocalNotification(
  input: CrmLocalNotificationInput = {},
): CrmLocalNotification {
  const id = input.id || input.messageId || undefined;
  return {
    ...(id ? { id } : {}),
    titulo: input.titulo || 'Notificación',
    mensaje: input.mensaje || '',
    data: input.data ?? {},
    detalles: input.detalles || '',
    onClick: resolveCrmOnClick(input.origin, input.ruta),
  };
}

async function postCrmLocalNotification(payload: CrmLocalNotification): Promise<boolean> {
  try {
    const response = await fetch(CRM_LOCAL_SYNC_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function notifyCrmLocal(
  input: CrmLocalNotification | CrmLocalNotificationInput,
): Promise<boolean> {
  const payload =
    'onClick' in input && input.onClick
      ? (input as CrmLocalNotification)
      : buildCrmLocalNotification(input);

  const fingerprint = crmNotificationFingerprint(payload);
  const send = async () => {
    if (!shouldDeliverCrmNotification(fingerprint)) return true;
    return postCrmLocalNotification(payload);
  };

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks && typeof locks.request === 'function') {
    return locks.request(`profase-notify:${fingerprint}`, send);
  }
  return send();
}
